import React from 'react';
import { PropertyRentProps } from '../../models/propertyModel';
import useModalState from '../../../../hooks/useModalState';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Row,
  Spinner,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  getPropertyStatus,
  getStatusBadgeColor,
} from '../../adapters/propertyAdapter';
import { Controller } from 'react-hook-form';
import {
  PROPERTY_MONTHS_LIST,
  PROPERTY_STATUS_VALUES,
} from '../../constants/propertyConstants';
import TakeDownPropertyModal from '../TakeDownPropertyModal';
import ActivatePropertyModal from '../ActivatePropertyModal';
import usePropertyRentPrices from '../../hooks/usePropertyRentPrices';
import Select from 'react-select';
import MoneyInput from '../../../../components/MoneyInput';

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  GTQ: 'Q',
  PAB: 'B/.',
};

const SectionTitle = ({
  title,
  hint,
  className = '',
}: {
  title: string;
  hint?: string;
  className?: string;
}) => (
  <div className={`mb-2 border-bottom pb-1 ${className}`}>
    <h5 className="text-uppercase text-muted ls-1 mb-1">{title}</h5>
    {hint && <small style={{ color: '#525f7f' }}>{hint}</small>}
  </div>
);

// "Disponible en 11 meses" para que una fecha lejana no pase desapercibida.
const availabilityHint = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  if (isNaN(date.getTime())) return null;
  const today = new Date();
  const months =
    (date.getFullYear() - today.getFullYear()) * 12 +
    (date.getMonth() - today.getMonth());
  if (date <= today) return { text: 'Disponible ahora', far: false };
  if (months < 1) return { text: 'Disponible en menos de un mes', far: false };
  return {
    text: `Disponible en ${months} ${months === 1 ? 'mes' : 'meses'}`,
    far: months >= 6,
  };
};

export const PropertyRentPrices = (props: PropertyRentProps) => {
  const navigate = useNavigate();
  const [takeDownPropertyModal, toggleTakeDownPropertyModal] = useModalState();
  const [activatePropertyModal, toggleActivatePropertyModal] = useModalState();
  const {
    // form
    handleSubmit,
    control,
    errors,
    setValue,
    watch,
    currency,
    // loading
    isLoading,
    loadingPlan,
    currentPlan,
    // catalogs
    catalogs,
    // actions
    updateMarketplaceRentPrices,
    updateMarketplaceRentStatus,
  } = usePropertyRentPrices({
    ...props,
  });

  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const selectedMonths = (watch('months') ?? []).map((month) => Number(month));
  const availability = availabilityHint(watch('available_at'));
  const isPublished =
    props.property_rent_state === PROPERTY_STATUS_VALUES.PUBLISHED;

  const toggleMonth = (month: number) => {
    const next = selectedMonths.includes(month)
      ? selectedMonths.filter((item) => item !== month)
      : [...selectedMonths, month].sort((a, b) => a - b);
    setValue('months', next, { shouldValidate: true });
  };

  return (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between py-3">
        <h3 className="mb-0">Configurar renta</h3>
        <Badge color={getStatusBadgeColor(props.property_rent_state)}>
          {getPropertyStatus(props.property_rent_state)}
        </Badge>
      </CardHeader>

      <Form role="form" onSubmit={handleSubmit(updateMarketplaceRentPrices)}>
        <CardBody className="py-3">
          <SectionTitle title="Precios y condiciones" />
          <Row>
            <Col lg="3" md="6">
              <FormGroup className="mb-3">
                <label className="form-control-label" htmlFor="price">
                  Precio de renta
                </label>
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <MoneyInput
                      id="price"
                      symbol={symbol}
                      suffix="/mes"
                      value={field.value}
                      onChange={field.onChange}
                      invalid={!!errors.price?.message}
                    />
                  )}
                />
                {errors.price?.message && (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.price.message}
                  </h5>
                )}
              </FormGroup>
            </Col>
            <Col lg="3" md="6">
              <FormGroup className="mb-3">
                <label className="form-control-label" htmlFor="min_price">
                  Precio mínimo de renta
                </label>
                <Controller
                  name="min_price"
                  control={control}
                  render={({ field }) => (
                    <MoneyInput
                      id="min_price"
                      symbol={symbol}
                      suffix="/mes"
                      value={field.value}
                      onChange={field.onChange}
                      invalid={!!errors.min_price?.message}
                    />
                  )}
                />
                {errors.min_price?.message ? (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.min_price.message}
                  </h5>
                ) : (
                  <small style={{ color: '#525f7f' }}>
                    Lo mínimo que aceptarías en una negociación.
                  </small>
                )}
              </FormGroup>
            </Col>
            {/* available at */}
            <Col lg="3" md="6">
              <FormGroup className="mb-3">
                <label className="form-control-label" htmlFor="available_at">
                  ¿Desde cuándo puede ser alquilado?
                </label>
                <Controller
                  name="available_at"
                  control={control}
                  render={({ field }) => (
                    <Input
                      className={`text-dark ${
                        errors.available_at?.message ? 'is-invalid' : ''
                      }`}
                      {...field}
                      value={field.value ?? ''}
                      id="available_at"
                      type="date"
                    />
                  )}
                />
                {errors.available_at?.message ? (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.available_at.message}
                  </h5>
                ) : (
                  availability && (
                    <small
                      className={
                        availability.far ? 'text-warning' : 'text-muted'
                      }
                    >
                      {availability.far && (
                        <i className="fa-solid fa-triangle-exclamation mr-1" />
                      )}
                      {availability.text}
                    </small>
                  )
                )}
              </FormGroup>
            </Col>
            {/* Plan */}
            <Col lg="3" md="6">
              <FormGroup className="mb-3">
                <label className="form-control-label" htmlFor="plan_id">
                  Plan
                </label>
                {!loadingPlan && (
                  <Controller
                    name="plan_id"
                    control={control}
                    defaultValue={currentPlan.value || ''}
                    render={({ field: { ref } }) => (
                      <Select
                        placeholder="Selecciona un plan"
                        options={catalogs.plans}
                        defaultValue={currentPlan}
                        onChange={(selected: any) => {
                          const { value = '' } = selected;
                          setValue('plan_id', value);
                        }}
                        ref={ref}
                      />
                    )}
                  />
                )}
                {errors.plan_id?.message && (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.plan_id.message}
                  </h5>
                )}
              </FormGroup>
            </Col>
            {/* months */}
            <Col md="12">
              <FormGroup className="mb-3">
                <label className="form-control-label d-block">
                  Meses para contratos
                </label>
                {PROPERTY_MONTHS_LIST.map(({ value, label }) => {
                  const isActive = selectedMonths.includes(Number(value));
                  return (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      color="primary"
                      outline={!isActive}
                      className="mr-2 mb-1"
                      aria-pressed={isActive}
                      onClick={() => toggleMonth(Number(value))}
                    >
                      {label}
                    </Button>
                  );
                })}
                {errors.months?.message && (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.months.message}
                  </h5>
                )}
              </FormGroup>
            </Col>
          </Row>

          <SectionTitle
            className="mt-2"
            title="Visibilidad en marketplace"
            hint={
              isPublished
                ? 'La propiedad se está mostrando en el marketplace de rentas.'
                : 'La propiedad no se muestra en el marketplace de rentas.'
            }
          />
          {isPublished ? (
            <Button
              type="button"
              onClick={toggleTakeDownPropertyModal}
              color="danger"
              outline
            >
              <i className="fa-solid fa-eye-slash mr-2" />
              Ocultar en marketplace
            </Button>
          ) : (
            <Button
              type="button"
              onClick={toggleActivatePropertyModal}
              color="success"
              outline
            >
              <i className="fa-solid fa-eye mr-2" />
              Publicar en marketplace
            </Button>
          )}
        </CardBody>
        <CardFooter className="text-right py-3">
          <Button
            type="button"
            color="secondary"
            outline
            className="mr-2"
            onClick={() => navigate(`/properties/admin/${props.property_id}`)}
          >
            Cancelar
          </Button>
          <Button color="primary" disabled={isLoading}>
            {isLoading && <Spinner size="sm" className="mr-2" />}
            Guardar cambios
          </Button>
        </CardFooter>
      </Form>
      <TakeDownPropertyModal
        marketplace_type="rent"
        property_id={props.property_id}
        isOpen={takeDownPropertyModal}
        onToggle={toggleTakeDownPropertyModal}
        onConfirmation={updateMarketplaceRentStatus}
        warningText={
          '¿Estás seguro de quitar la propiedad del marketplace de rentas?'
        }
        confirmationText={'Si, quitar del marketplace rentas'}
        abortText={'No, continuar revisando...'}
      />
      <ActivatePropertyModal
        marketplace_type="rent"
        property_id={props.property_id}
        isOpen={activatePropertyModal}
        onToggle={toggleActivatePropertyModal}
        onConfirmation={updateMarketplaceRentStatus}
        warningText={
          'Esta acción hará que tu publicación sea accedida desde la marketplace propiweb, asi como recibir visitas y/o ofertas'
        }
        confirmationText={'Si, activar del marketplace rentas'}
        abortText={'No, continuar revisando...'}
      />
    </Card>
  );
};
